import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    AfterViewInit,
    OnDestroy,
    ViewChildren,
    QueryList,
    ElementRef,
} from '@angular/core';
import { TriStateCheckboxesFlow } from '@emi/shared/constants';
import { CheckboxTreeNode, CheckboxState } from '@emi/shared/models';
import { Observable, Subscription } from 'rxjs';

@Component({
    selector: 'via-checkbox-tree',
    templateUrl: './checkbox-tree.component.html',
    styleUrls: ['./checkbox-tree.component.scss'],
})
export class CheckboxTreeComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChildren('treeNodeRef')
    public treeNodes: QueryList<ElementRef>;

    @Input()
    public tree: CheckboxTreeNode[];

    @Input()
    public triStateCheckboxesEnabled = false;

    @Input()
    public triStateCheckboxesFlow: number = TriStateCheckboxesFlow.DEFUALT;

    @Input()
    public branchCount = 1;

    @Input()
    public accordionStyle = false;

    @Input()
    public events: Observable<CheckboxTreeNode[]>;

    @Output()
    public selectedSingularNodeChange = new EventEmitter<CheckboxTreeNode>();

    @Output()
    public onChange = new EventEmitter();

    @Output()
    public afterViewInit = new EventEmitter();

    public treeBranches: CheckboxTreeNode[][];
    private eventsSubscription: Subscription;
    private treeNodesMap = new Map<string, ElementRef>();

    public ngOnInit(): void {
        this.tree = this.setParentIds(this.tree);

        // Split the tree for creating columns in the html
        this.treeBranches = this.splitArray(this.tree, this.branchCount);

        this.eventsSubscription = this.events?.subscribe((updatedTree) => {
            if (updatedTree) {
                this.tree = this.setParentIds(updatedTree);
                this.treeBranches = this.splitArray(this.tree, this.branchCount);
            }
        });
    }

    // Create child-parent awareness in tree
    private setParentIds(tree: CheckboxTreeNode[]): CheckboxTreeNode[] {
      if (this.triStateCheckboxesEnabled) {
            for (let node of tree) {
                this.setParentId(node);
            }
        }
        return tree;
    }

    private setParentId(node: CheckboxTreeNode, parentId?: number): void {
        node.parentId = parentId;
        if (node.children) {
            node.children.forEach((n) => this.setParentId(n, node.id));
        }
    }

    private splitArray(array: any[], parts: number): any[] {
        const partSize = Math.ceil(array.length / parts);
        const result: any[] = [];
        for (let i = 0; i < array.length; i += partSize) {
            result.push(array.slice(i, i + partSize));
        }
        return result;
    }

    public toggleTreeNode(node: CheckboxTreeNode): void {
        node.isExpanded = !node.isExpanded;
    }

    public selectionChanged(selectedNode: CheckboxTreeNode): void {
        // Update tree for view
        for (let node of this.tree) {
            this.updateTreeNode(node, selectedNode);
        }
        this.tree = [...this.tree];

        this.selectedSingularNodeChange.emit(this.buildSelection(selectedNode));
        this.onChange.emit();
    }

    // Traverse the tree to find the selected node
    private updateTreeNode(node: CheckboxTreeNode, selectedNode: CheckboxTreeNode): void {
        if (node.id === selectedNode.id && node.industryCode == selectedNode.industryCode) {
            node.state = node.state !== 'checked' ? 'checked' : 'unchecked';
            this.updateChildNode(node, node.state);

            if (this.triStateCheckboxesEnabled && selectedNode.parentId && this.triStateCheckboxesFlow === TriStateCheckboxesFlow.DEFUALT) {
                this.updateParentNode(selectedNode.parentId);
            }

            if (this.triStateCheckboxesEnabled && selectedNode.parentId && this.triStateCheckboxesFlow === TriStateCheckboxesFlow.ALTERNATIVE_CHECKBOXES) {
              this.updateParentNodeAlternative(selectedNode.parentId);
          }
            return;
        }
        if (this.hasChildren(node)) {
            node.children!.forEach((x) => {
                this.updateTreeNode(x, selectedNode);
            });
        }
    }

    public hasChildren(node: CheckboxTreeNode): boolean | undefined {
        return node.children && node.children.length > 0;
    }

    // Traverse down the tree from the selected node and set children checkbox states
    private updateChildNode(node: CheckboxTreeNode, state: CheckboxState): void {
        if (this.hasChildren(node)) {
            node.children!.forEach((x) => {
                if (x.state !== 'disabled') {
                    x.state = state;
                }
                this.updateChildNode(x, state);
            });
        }
    }

    // Traverse up the tree from the selected node and set parent checkbox states
    // (only used when tri-state checkboxes flag enabled)
    private updateParentNode(parentId: number): void {
        const parentNode = this.getParentNode(this.tree, parentId);

        if (parentNode && parentNode.state !== 'disabled' && parentNode.children) {
            const siblings: CheckboxTreeNode[] = parentNode.children;

            if (siblings.every((x) => x.state === 'checked') || siblings.every((x) => x.state === 'unchecked')) {
                parentNode.state = siblings[0].state;
            } else if (
                siblings.some(
                    (x) =>
                        x.state === 'indeterminate' ||
                        (siblings.some((x) => x.state === 'checked') && siblings.some((x) => x.state === 'unchecked'))
                )
            ) {
                parentNode.state = 'indeterminate';
            }

            if (parentNode.parentId) {
                this.updateParentNode(parentNode.parentId);
            }
        }
    }
    private updateParentNodeAlternative(parentId: number): void {
      const parentNode = this.getParentNode(this.tree, parentId);
      if (parentNode && parentNode.state !== 'disabled' && parentNode.children) {
          const siblings: CheckboxTreeNode[] = parentNode.children;
          if (siblings.every((x) => x.state === 'unchecked')) {
              parentNode.state = siblings[0].state;
          } else if (
              siblings.some(
                  (x) =>
                      x.state === 'indeterminate' ||
                      (siblings.some((x) => x.state === 'checked'))
              )
          ) {
              parentNode.state = 'indeterminate';
          }

          if (parentNode.parentId) {
              this.updateParentNode(parentNode.parentId);
          }
      }
  }
    private getParentNode(tree: CheckboxTreeNode[], parentId: number): CheckboxTreeNode | null {
        let parentNode: CheckboxTreeNode | null;

        for (let branch of tree) {
            parentNode = this.getNodeById(branch, parentId);
            if (parentNode) {
                return parentNode;
            }
        }
        return null;
    }

    private getNodeById(node: CheckboxTreeNode, id: number): CheckboxTreeNode | null {
        let result: CheckboxTreeNode | null = null;
        if (node.id === id) {
            return node;
        } else if (this.hasChildren(node)) {
            node.children?.some((n) => {
                result = this.getNodeById(n, id);
                return result;
            });
        }
        return result;
    }

    // Build tree nodes containing selected items
    private buildSelection(node: CheckboxTreeNode): CheckboxTreeNode {
        if (!node.children || node.children.length === 0) {
            return { ...node };
        }
        const newChildren = [];

        for (let child of node.children) {
            newChildren.push(this.buildSelection(child));
        }
        return { ...node, children: newChildren };
    }

    public ngOnDestroy(): void {
        this.eventsSubscription?.unsubscribe();
    }

    // Accessibility: handling keyboard events
    public keyEvents(event: KeyboardEvent, node: CheckboxTreeNode): void {
        switch (event.key) {
            case 'ArrowDown': {
                event.preventDefault();
                this.findNextNode(node);
                return;
            }
            case 'ArrowUp': {
                event.preventDefault();
                this.findPreviousNode(node);
                return;
            }
            case 'ArrowRight': {
                node.isExpanded = true;
                return;
            }
            case 'ArrowLeft': {
                node.isExpanded = false;
                return;
            }
            case 'Home': {
                this.focusNode(this.tree[0]);
                return;
            }
            case 'End': {
                event.preventDefault();
                this.focusLastNode(this.tree[this.tree.length - 1]);
                return;
            }
            case ' ':
            case 'Enter': {
                event.preventDefault();
                this.selectionChanged(node);
            }
        }
    }

    private findNextNode(node: CheckboxTreeNode, ignoreChildren = false): void {
        // If a node has expanded children, focus the first child
        if (this.hasChildren(node) && node.isExpanded && !ignoreChildren) {
            this.focusNode(node.children![0]);
            return;
        }

        let indexOfCurrentNode: number;
        let parentNode: CheckboxTreeNode | null = null;
        let nodes: CheckboxTreeNode[];

        if (node.parentId) {
            parentNode = this.getParentNode(this.tree, node.parentId);
            indexOfCurrentNode = parentNode!.children!.indexOf(node);
            nodes = parentNode!.children!;
        } else {
            indexOfCurrentNode = this.tree.indexOf(node);
            nodes = this.tree;
        }

        // If a next sibling exists focus it, otherwise look in parent nodes
        if (indexOfCurrentNode + 1 <= nodes.length - 1) {
            this.focusNode(nodes[indexOfCurrentNode + 1]);
        } else if (parentNode) {
            this.findNextNode(parentNode, true);
        }
    }

    private findPreviousNode(node: CheckboxTreeNode) {
        let indexOfCurrentNode: number;
        let parentNode: CheckboxTreeNode | null = null;
        let prevNode: CheckboxTreeNode;

        if (node.parentId) {
            parentNode = this.getParentNode(this.tree, node.parentId);
            indexOfCurrentNode = parentNode!.children!.indexOf(node);
            prevNode = parentNode!.children![indexOfCurrentNode - 1];
        } else {
            indexOfCurrentNode = this.tree.indexOf(node);
            prevNode = this.tree[indexOfCurrentNode - 1];
        }

        // If a previous sibling that is not expanded exists focus it, otherwise focus last previous child...
        if (indexOfCurrentNode - 1 >= 0) {
            if (prevNode.isExpanded) {
                this.focusNode(this.findLastChildNode(prevNode));
            } else {
                this.focusNode(prevNode);
            }
            // ...else focus the group's parent node.
        } else if (parentNode) {
            this.focusNode(parentNode);
        }
    }

    private findLastChildNode(node: CheckboxTreeNode): CheckboxTreeNode {
        if (node.children && node.children.length > 0 && node.isExpanded) {
            return this.findLastChildNode(node.children[node.children.length - 1]);
        }
        return node;
    }

    private focusLastNode(lastRootNode: CheckboxTreeNode): void {
        if (!lastRootNode.isExpanded) {
            this.focusNode(lastRootNode);
        } else {
            this.focusNode(this.findLastChildNode(lastRootNode));
        }
    }

    private focusNode(node: CheckboxTreeNode): void {
        const nodeElementRef = this.treeNodesMap.get(this.getNodeId(node));

        if (nodeElementRef) {
            nodeElementRef.nativeElement.focus();
        }
    }

    public getNodeId(node: CheckboxTreeNode): string {
        // TODO: refactor to use unique ID once duplicate category ID issue is solved.
        return String(node.id);
    }

    private createTreeNodesMap(): void {
        this.treeNodes.forEach((e) => this.treeNodesMap.set(e.nativeElement.getAttribute('data-node-id'), e));
    }

    public ngAfterViewInit(): void {
        this.createTreeNodesMap();

        this.treeNodes.changes.subscribe(() => {
            this.treeNodesMap.clear();
            this.createTreeNodesMap();
        });

        this.afterViewInit.emit();
    }
}
